import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Divider, Tabs, Tab, Badge, Box } from "@mui/material";
import EditOutlined from "@mui/icons-material/EditOutlined";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import DriverBasicDetail from "./driverComponents/DriverBasicDetail";
import DriverLicenceTabs from "./driverComponents/DriverLicenceTabs";
import DriverHistoryTabs from "./driverComponents/DriverHistoryTabs";
import WorkScheduleTabs from "./driverComponents/DriverWorkSchedule";

import StagePathway from "components/Shared/StagePathway";
import EnableDisableButton from "components/Shared/EnableDisableButton";

import {
  useGetDriverDetailQuery,
  useUpdateDriverMutation,
} from "store/services/driverApi";

import {
  DriverDetailContext,
  useDriverDetailContext,
} from "contexts/DriverDetailContext";

import { useAuth } from "hooks/useAuth";
import { useAppMeta } from "hooks";
import DriverAssignedShipmentsTabs from "./driverComponents/DriverAssignmentShipmentsTabs";
import DriverPaymentTab from "./driverComponents/DriverPaymentTab";
import DriverActivityChart from "./driverComponents/DriverActivityChart";
import DriverAssignedRoutesTabs from "./driverComponents/DriverAssignedRouteTabs";

const DriverDetailScreen = () => {
  const { id } = useParams();

  const { isAdmin } = useAuth();

  const [activeTab, setActiveTab] = useState(0); // Local state for tabs

  // RTK Query
  const {
    data: driverDetail,
    isLoading: loadingDriver,
    error,
  } = useGetDriverDetailQuery(id);

  useAppMeta(
    !driverDetail ? "Driver Detail" : `Driver - ${driverDetail?.subject}`
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Function to handle driver stage changes
  const [updateDriver] = useUpdateDriverMutation();

  const handleChangeDriverStage = async (stage) => {
    try {
      await updateDriver({
        _id: driverDetail?._id,
        stage,
        driverComment: `Changed stage from "${driverDetail?.stage}" to "${stage}"`,
      }).unwrap();
      toast.info("Driver's stage updated!");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update driver's stage!");
    }
  };

  const breadcrumbs = [
    ...(isAdmin
      ? [
          {
            title: "Drivers",
            path: "/app/crm/driver/list",
          },
        ]
      : []),
    { title: "Driver Detail" },
  ];

  // Define tab labels and badge counts
  const tabs = [
    {
      label: "Assigned Shipment",
    },
    {
      label: "Route Assigned",
      badgeCount: driverDetail?.routeAssignments?.length,
    },
    {
      label: "Schedule",
      badgeCount: driverDetail?.work_schedule?.length,
    },
    {
      label: "History",
      badgeCount: driverDetail?.driverHistory?.length,
    },
    {
      label: "Licence",
      badgeCount: driverDetail?.driverLicence?.length,
    },
    {
      label: "Remaining Payment",
      badgeCount: driverDetail?.driverLicence?.length,
    },
    {
      label: "Statistics",
    },
  ];

  useAppMeta("Driver Detail");

  return (
    <DriverDetailContext.Provider value={{ driverDetail, loadingDriver }}>
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        isBusy={loadingDriver}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />

        {isAdmin && !!driverDetail?.stage && (
          <StagePathway
            activeStage={driverDetail?.stage}
            getStageLabel={(stage) => stage}
            getStageValue={(stage) => stage}
            onChange={handleChangeDriverStage}
          />
        )}

        <BreadcrumbLayout.Paper>
          <DriverBasicDetail />
          <Divider />

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Driver Detail Tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={
                  <Badge
                    badgeContent={tab.badgeCount || 0}
                    color="primary"
                    invisible={!tab.badgeCount}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    sx={{
                      "& .MuiBadge-badge": {
                        transform: "scale(1) translate(30%, -30%)",
                        top: -4,
                        right: 4,
                      },
                    }}
                  >
                    <Box component="span" sx={{ pr: 2 }}>
                      {tab.label}
                    </Box>
                  </Badge>
                }
              />
            ))}
          </Tabs>

          <Box sx={{ mt: 3 }}>
            {activeTab === 0 && <DriverAssignedShipmentsTabs />}
            {activeTab === 1 && <DriverAssignedRoutesTabs />}
            {activeTab === 2 && <WorkScheduleTabs />}
            {activeTab === 3 && <DriverHistoryTabs />}
            {activeTab === 4 && <DriverLicenceTabs />}
            {activeTab === 5 && <DriverPaymentTab />}
            {activeTab === 6 && <DriverActivityChart driverId={driverDetail?._id}/>}
          </Box>
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </DriverDetailContext.Provider>
  );
};

const PageActions = () => {
  const { isAdmin, isAccountAdmin } = useAuth();
  const { driverDetail } = useDriverDetailContext();
  const [updateDriver, { isLoading: updatingDriver }] =
    useUpdateDriverMutation();

  const handleUpdateStatus = async () => {
    try {
      await updateDriver({
        _id: driverDetail?._id,
        isActive: !driverDetail?.isActive,
        driverComment: `Changed the status to "${driverDetail?.isActive ? "Inactive" : "Active"
          }"`,
      }).unwrap();
      toast.info(
        `Driver successfully ${driverDetail?.isActive ? "Disabled" : "Enabled"
        }!`
      );
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update driver's status!");
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="flex space-x-4">
      {isAccountAdmin && (<EnableDisableButton
        suffix="Driver"
        isActive={driverDetail?.isActive}
        loading={updatingDriver}
        onClick={handleUpdateStatus}
      />)}
      <Link to={`/app/crm/driver/${driverDetail?._id}/edit`}>
        <Button variant="contained" startIcon={<EditOutlined />}>
          Edit
        </Button>
      </Link>
    </div>
  );
};

export default DriverDetailScreen;